/**
 * Something to point a camera at.
 *
 * **The encoder is not here.** `qrSvg()` in `@rentorng/core` is the single implementation of the
 * part that can be got wrong — Reed–Solomon over GF(256), version selection, the data mask and the
 * two BCH-coded bit fields — and the console has a wrapper of its own that looks very like this
 * one. That is the split the loader already uses: the geometry lives once in a package with no
 * React in it, and each surface wraps it in a dozen lines of its own layout.
 *
 * It is not one shared *component* because the console deliberately imports nothing from this
 * application and nothing here imports from it (ADR-0077, and the header of `apps/admin/src/ui.tsx`).
 * A QR code is not the exception worth making: what would drift if it were copied is the encoder,
 * and the encoder is not copied.
 *
 * **Black on white, with its quiet zone, on any ground.** The symbol carries four modules of light
 * inside the SVG and this box adds a little more around it. Tinting a code to match a brand and
 * letting a card crop its margin are the two commonest reasons a symbol that looks perfect will
 * not scan, so neither is offered — there is no colour prop and there is no way to make the box
 * smaller than the size `tools/verify-qr.mjs` proves decodable.
 *
 * ```tsx
 * <QrCode value={enrolment.uri} label="Scan to add this account to your authenticator app" />
 * ```
 */

import { qrSvg } from "@rentorng/core";

export function QrCode({
  value,
  label,
  className = "",
}: {
  /** Exactly what the scanner should read back. Reproduced byte for byte. */
  value: string;
  /**
   * The accessible name. Say what scanning it will do rather than that it is a QR code — somebody
   * on a screen reader is being told what they would miss, not what shape is on the page.
   */
  label: string;
  /** Layout only — margins and alignment. The size belongs to the component. */
  className?: string;
}) {
  return (
    <div className={`inline-block rounded-[var(--radius-surface)] bg-white p-2 ${className}`.trim()}>
      <div className="h-52 w-52" dangerouslySetInnerHTML={{ __html: qrSvg(value, label) }} />
    </div>
  );
}
